import { useCollection } from "@cloudscape-design/collection-hooks";
import {
  AppLayout,
  Box,
  BreadcrumbGroup,
  Button,
  Header,
  HelpPanel,
  Pagination,
  PropertyFilter,
  SpaceBetween,
  Table,
} from "@cloudscape-design/components";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchplhistory } from "../redux/actions/plhistory";
import { useNavigate } from "react-router-dom";
import {
  PropertyFilteri18nStrings,
  paginationLabels,
} from "./components/labels";
import EmptyState from "./components/EmptyState";
import NotificationFlashbar from "./components/NotificationFlashbar";
import GitHubLinks from "./components/GitHubLinks";
import Navigation from "./components/Navigation";
import AwsLoginModal from "./modals/AwsLoginModal";

const PLHistoryModals = () => {
  const modal = useSelector((state) => state.modal);
  const dispatch = useDispatch();

  return (
    <Box>
      <AwsLoginModal />
    </Box>
  );
};

///////////////
// ITEM TABLE
///////////////

const ItemTable = () => {
  const [forceRefresh, setForceRefresh] = useState(false);
  const Config = useSelector((state) => state.config);
  const Items = useSelector((state) => state.plhistory);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    propertyFilterProps,
    paginationProps,
  } = useCollection(Items.items, {
    propertyFiltering: {
      filteringProperties: [
        {
          propertyLabel: "Event ID",
          key: "eventId",
          operators: [":", "!:", "=", "!="],
        },
        {
          propertyLabel: "Event Type",
          key: "eventType",
          operators: [":", "!:", "=", "!="],
        },
        {
          propertyLabel: "Additional Info",
          key: "additionalInfo",
          operators: [":", "!:", "=", "!=", "<", ">"],
        },
        {
          propertyLabel: "Event Date",
          key: "eventTimestamp",
          operators: ["=", "!=", ":", "!:"],
        },
      ],
      empty: (
        <EmptyState
          title="No History Record"
          subtitle="No History Record found."
          action={<Button disabled>Create History</Button>}
        />
      ),
      noMatch: (
        <EmptyState
          title="No matches"
          subtitle="We cannot find a matching history record."
          action={
            <Button
              onClick={() =>
                actions.setPropertyFiltering({ tokens: [], operation: "and" })
              }
            >
              Clear filters
            </Button>
          }
        />
      ),
    },
    pagination: { pageSize: Config.ITEM_PAGE_SIZE },
    sorting: {},
    selection: {
      trackBy: "eventId",
    },
  });

  /////////////////
  // LAZY LOADING
  /////////////////

  useEffect(() => {
    const refreshTimer = setInterval(() => {
      dispatch(fetchplhistory(false));
    }, Config.UPDATE_WEBSITE_INTERVAL * 5000);
    return () => clearInterval(refreshTimer);
  }, [dispatch, Config]);

  useEffect(() => {
    dispatch(fetchplhistory());
  }, [forceRefresh, dispatch]);

  useEffect(() => {
    if (collectionProps.selectedItems.length === 1) {
      dispatch({
        type: "selection/set",
        id: collectionProps.selectedItems[0].id,
      });
    } else {
      dispatch({ type: "selection/dismiss" });
    }
  }, [forceRefresh, dispatch, collectionProps.selectedItems]);

  const refreshNow = () => {
    setForceRefresh((refresh) => !refresh);
  };

  //////////
  // TABLE
  //////////

  return (
    <Box>
      <Table
        {...collectionProps}
        columnDefinitions={[
          {
            id: "eventId",
            header: "Event ID",
            sortingField: "eventId",
            cell: (item) => item.eventId,
          },
          {
            id: "eventType",
            header: "Event Type",
            sortingField: "eventType",
            cell: (item) => item.eventType,
          },
          {
            id: "additionalInfo",
            header: "Created On",
            sortingField: "additionalInfo",
            cell: (item) => item.additionalInfo,
          },
          {
            id: "eventTimestamp",
            header: "Event Date",
            sortingField: "eventTimestamp",
            cell: (item) => item.eventTimestamp,
          },
        ]}
        items={items}
        selectionType="multi"
        header={
          <Header
            variant="h1"
            selectedItems={collectionProps.selectedItems}
            counter={"(" + Items.items.length + ")"}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button iconName="refresh" onClick={refreshNow} />
              </SpaceBetween>
            }
          >
            PipeLine History
          </Header>
        }
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            i18nStrings={PropertyFilteri18nStrings}
            filteringPlaceholder="Find history record"
            countText={
              filteredItemsCount +
              " match" +
              (filteredItemsCount === 1 ? "" : "es")
            }
          />
        }
        pagination={
          <Pagination {...paginationProps} ariaLabels={paginationLabels} />
        }
        loading={Items.status === "loading"}
      />
      <PLHistoryModals />
    </Box>
  );
};

//////////////////
// APP + CONTENT
//////////////////

const OverviewPipeLineHistory = () => {
  const [splitPanelSize, setSplitPanelSize] = useState(500);
  const [splitPanelOpen, setSplitPanelOpen] = useState(false);
  const [hasManuallyClosedOnce, setHasManuallyClosedOnce] = useState(false);
  const hasSelection = useSelector(
    (state) => state.selection.status === "selected"
  );
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: "notification/dismiss" });
    dispatch({ type: "selection/dismiss" });
    dispatch({ type: "modal/dismiss" });
  }, [dispatch]);

  useEffect(() => {
    if (hasSelection && !hasManuallyClosedOnce) {
      setSplitPanelOpen(true);
    }
  }, [hasSelection, hasManuallyClosedOnce]);

  const onSplitPanelResize = ({ detail: { size } }) => {
    setSplitPanelSize(size);
  };

  const onSplitPanelToggle = ({ detail: { open } }) => {
    setSplitPanelOpen(open);

    if (!open) {
      setHasManuallyClosedOnce(true);
    }
  };

  return (
    <AppLayout
      navigation={<Navigation />}
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: "Admin", href: "#" },
            { text: "History", href: "#/plhistory" },
          ]}
        />
      }
      contentType="default"
      tools={<SideHelp />}
      onSplitPanelToggle={onSplitPanelToggle}
      splitPanelOpen={splitPanelOpen}
      splitPanelSize={splitPanelSize}
      onSplitPanelResize={onSplitPanelResize}
      stickyNotifications
      notifications={<NotificationFlashbar />}
      content={<ItemTable />}
    />
  );
};

const SideHelp = () => {
  const Config = useSelector((state) => state.config);
  return (
    <HelpPanel header={<h2>UCI Student AWS Accounts</h2>}>
      <Box>
        <Header variant="h3">History Records</Header>
        <SpaceBetween size="m">
          <Box>
            Pipeline History records shows the activity happened while
            invoking/requesting pipeline.
          </Box>
        </SpaceBetween>
      </Box>
      <hr />
      <hr />
      <GitHubLinks />
    </HelpPanel>
  );
};

export default OverviewPipeLineHistory;
